import React, { useEffect, useRef, useState } from "react";
import { PostStepProps } from "../Create";
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputRef,
	Radio,
	Row,
	Select,
	Space,
	Spin,
	Typography,
	message,
} from "antd";
import { generateFormRules, handleSubmitFailed, resetErrorFormItem } from "../../../../helpers/formRules";
import { getErrorMessage } from "../../../../helpers/errorHandler";
import useAuth from "../../../../hooks/useAuth";
import { generateSlugBlog } from "../../../../helpers/generator";
import {
	ArticleProperties,
	EArticleCategoriesStatus,
	EArticleStatus,
	EPublishTypeArticle,
	ETagStatus,
	MasterArticleCategoryProperties,
	TagProperties,
} from "../../../../types/blog.type";
import { httpRequest } from "../../../../helpers/api";
import { BaseResponsePaginationProps, BaseResponseProps } from "../../../../types/config.type";
import UploadImageBlog, { FormValuesUploadImage } from "./UploadImageBlog";
import SelectCategories from "./SelectCategories";
import { confirmModal, successModal } from "../../../../helpers/modal";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { useDebounce } from "use-debounce";
import { RangePickerProps } from "antd/lib/date-picker";
import { urlBlog } from "../helpers/urlBlog";
import { PlusOutlined } from "@ant-design/icons";
import SELECT_SEARCH_PROPS from "../../../../helpers/selectSearch";
import CreateCategoryModal from "./CreateCategoryModal";
import { getFullName } from "../../../../helpers/name";

const { Text } = Typography;

interface FormProps {
	title: string;
	subtitle: string;
	authorName?: string;
	categoryIds: string[];
	parentCategoryIds: string[];
	images: FormValuesUploadImage[];
	tagIds: string[];
	publicationAt: Date;
	status: EArticleStatus;
	publishType: EPublishTypeArticle;
}

const CreateDetailPost: React.FC<PostStepProps> = ({ articleId, post, isEdit, setPost }) => {
	const { employeeId } = useAuth();
	const [form] = Form.useForm<FormProps>();
	const [tags, setTags] = useState<TagProperties[]>([]);
	const [categories, setCategories] = useState<MasterArticleCategoryProperties[]>([]);
	const { user } = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	const [isFetch, setIsFetch] = useState(true);
	const [showModalAddCategory, setShowModalAddCategory] = useState(false);

	const titleWatch = Form.useWatch("title", form);
	const [debouncedSlug] = useDebounce(titleWatch, 500);
	let slugWatch = generateSlugBlog(debouncedSlug);
	const choosePublishWatch = Form.useWatch("publishType", form);
	const parentCategoryWatch = Form.useWatch("parentCategoryIds", form);

	useEffect(() => {
		if (titleWatch && titleWatch.length > 0) {
			let slug = generateSlugBlog(titleWatch);
			form.setFieldValue("urlPreview", `${urlBlog}/article/${slug}`);
		}
	}, [titleWatch]);

	const history = useHistory();
	const [slugStatus, setSlugStatus] = useState<undefined | "available" | "checking" | "unavailable" | "error">(
		undefined
	);

	const _handleCheckSlug = async () => {
		const { data } = await httpRequest.post<BaseResponseProps<ArticleProperties>>("articles/check-slug", {
			slug: slugWatch,
		});

		if (data.payload) {
			if (data.payload.slug === post?.slug) {
				setSlugStatus("available");
			} else {
				setSlugStatus("unavailable");
			}
		} else {
			setSlugStatus("available");
		}
	};

	useEffect(() => {
		if (debouncedSlug) {
			_handleCheckSlug();
		} else {
			setSlugStatus(undefined);
		}

		// eslint-disable-next-line
	}, [debouncedSlug]);

	useEffect(() => {
		if (isFetch) {
			(async () => {
				setIsLoading(true);
				try {
					const resCategories = await httpRequest.get<
						BaseResponsePaginationProps<MasterArticleCategoryProperties>
					>(`/master-article-categories?status=${EArticleCategoriesStatus.PUBLISH}`);
					const resTags = await httpRequest.get<BaseResponsePaginationProps<TagProperties>>(
						`/master-article-tags?status=${ETagStatus.PUBLISH}`
					);

					setCategories(resCategories.data.payload.results);
					setTags(resTags.data.payload.results);
				} catch (error) {
					console.log(error);
					message.error(getErrorMessage(error));
				} finally {
					setIsLoading(false);
				}
			})();

			setIsFetch(false);
		}
	}, [isFetch]);

	useEffect(() => {
		if (post) {
			form.setFieldsValue({
				title: post.title,
				authorName: post.authorName,
				subtitle: post.subtitle,
				categoryIds: post.categories
					.map((cat) => cat.categoryId && `${cat.category.parentCategoryId}_${cat.categoryId}`)
					.filter(Boolean),
				parentCategoryIds: post.categories
					.map((cat) => !cat.categoryId && `${cat.parentCategoryId}`)
					.filter(Boolean),
				tagIds: post.tags?.map((tag) => tag.tagId),
				publicationAt: moment(post.publicationAt),
				images:
					post.images && post.images.length > 0
						? post.images.map((image: any) => ({
								id: image.fileId,
								file: {
									fileUrl: image.imageUrl,
									originalName: image.metadata.originalName,
									mimeType: image.metadata.mimeType,
								},
								imageName: image.metadata.imageName,
								captionImage: image.metadata.captionImage,
						  }))
						: [],
				publishType: post?.publishType,
			});
		} else {
			form.resetFields();
		}
	}, [post]);

	const _handleSubmit = async (type: "next" | "draft") => {
		if (slugStatus !== "available") {
			message.error("URL already used!");
			return;
		}

		try {
			await form.validateFields();
		} catch (error: any) {
			console.error(error);
			handleSubmitFailed(error);
			return;
		}

		setIsLoading(true);

		try {
			const values = form.getFieldsValue();

			if (values?.categoryIds?.length === 0 && values?.parentCategoryIds?.length === 0) {
				return message.error("Categories is required.");
			}

			let dto: any = {
				...values,
				title: values.title,
				subtitle: values.subtitle,
				authorName: values.authorName,
				publicationAt: isEdit
					? post?.publicationAt
					: values?.publishType === EPublishTypeArticle.PUBLISH_NOW
					? moment()
					: values.publicationAt,
				status: isEdit ? post?.status : EArticleStatus.OPEN,
				categoryIds: values.categoryIds.map((catId) => catId?.split("_")[1]),
				parentCategoryIds: values?.parentCategoryIds.map((catId) => catId),
			};

			if (!isEdit) {
				dto = {
					...dto,
					externalData: {
						employeeId,
					},
				};
			}
			var postResults: ArticleProperties;

			try {
				if (articleId) {
					let { data } = await httpRequest.patch<BaseResponseProps<ArticleProperties>>(
						"/articles/" + articleId,
						dto
					);
					postResults = data.payload;
				} else {
					let { data } = await httpRequest.post<BaseResponseProps<ArticleProperties>>("/articles", {
						...dto,
					});
					postResults = data.payload;
				}
			} catch (error) {
				message.error(getErrorMessage(error));
				setIsLoading(false);
				return;
			}

			if (values.images && values.images.length > 0 && values.images[0].file instanceof File) {
				if (values.images[0].hasOwnProperty("id")) {
					try {
						let formData = new FormData();
						formData.append("image", values.images[0].file);
						formData.append("imageName", values.images[0].imageName);
						formData.append("captionImage", values.images[0]?.captionImage || "");

						let resImage: any = await httpRequest.post(
							"/articles/image/" + postResults.articleId + `/${undefined}`,
							formData
						);
						postResults.images = [
							{
								imageUrl: resImage.data.payload.fileLinkCache,
								fileId: resImage.data.payload.fileId,
								metadata: resImage.data.payload.metadata,
							},
						];
					} catch (error) {
						message.error(getErrorMessage(error));
						setIsLoading(false);
						if (type === "draft") {
							successModal({
								title: "Drafted!",
								subtitle: "Blog saved as draft!",
								onOk: () => {
									history.replace(`/blog-posts?activeTab=${EArticleStatus.OPEN}`);
								},
							});
						} else if (isEdit) {
							history.push("/blog-posts/" + postResults.articleId + "/edit/description-post");
						} else {
							history.push("/blog-posts/create/" + postResults.articleId + "/description-post");
						}
						setIsLoading(false);

						return;
					}
				}
			}

			if (type === "draft") {
				successModal({
					title: "Drafted!",
					subtitle: "Blog saved as draft!",
					onOk: () => {
						history.replace(`/blog-posts?activeTab=${EArticleStatus.OPEN}`);
					},
				});

				setIsLoading(false);

				return;
			} else if (isEdit) {
				return history.push("/blog-posts/" + postResults.articleId + "/edit/description-post");
			} else {
				return history.push("/blog-posts/create/" + postResults.articleId + "/description-post");
			}
		} catch (error) {
			console.log(error);
			message.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	// eslint-disable-next-line arrow-body-style
	const disabledDate: RangePickerProps["disabledDate"] = (current) => {
		// Can not select days before today and today
		return current && current < moment().startOf("day");
	};

	const [newTag, setNewTag] = useState("");
	const inputNewTagRef = useRef<InputRef>(null);

	const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setNewTag(event.target.value);
	};

	const addNewTag = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		e.preventDefault();
		if (!newTag) return message.error("Please enter new tag.");

		confirmModal({
			title: "New Tag",
			subtitle: `Are you sure you want to add "${newTag}" as a new tag?`,
			onOk: async () => {
				try {
					const res = await httpRequest.post<BaseResponseProps<TagProperties>>("/master-article-tags", {
						tagName: newTag,
						status: "PUBLISH",
					});

					setIsFetch(true);
					setNewTag("");
					setTimeout(() => {
						inputNewTagRef.current?.focus();
					}, 0);
					const values = form.getFieldsValue();
					const tagIds = values?.tagIds || [];
					form.setFieldValue("tagIds", [...tagIds, res.data.payload.tagId]);

					successModal("Successfully Add New Tag");
				} catch (error) {
					message.error(getErrorMessage(error));
				}
			},
		});
	};

	return (
		<Spin spinning={isLoading}>
			<Form
				form={form}
				layout="vertical"
				autoComplete="off"
				onFinishFailed={handleSubmitFailed}
				initialValues={{
					author: user?.nickName,
					urlPreview: urlBlog + "/article",
					parentCategoryIds: [],
					categoryIds: [],
					// publicationAt: moment(),
				}}
			>
				<Row gutter={[24, 0]}>
					<Col span={24}>
						<Form.Item
							name="title"
							label="Post Title"
							rules={generateFormRules("Post Title", ["required"])}
						>
							<Input placeholder="Enter post title.." />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="subtitle"
							label="Meta Description"
							rules={generateFormRules("Meta Description", ["required"])}
						>
							<Input.TextArea
								rows={2}
								placeholder="Enter meta description (max description 160 characters)"
								maxLength={160}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="urlPreview"
							label="URL Preview"
							validateStatus={
								slugStatus
									? slugStatus === "unavailable"
										? "error"
										: slugStatus === "available"
										? "success"
										: ""
									: ""
							}
							normalize={(value) => value?.toLowerCase()}
						>
							<Input
								readOnly
								disabled
							/>
						</Form.Item>
						{slugStatus ? (
							slugStatus === "unavailable" ? (
								<Text type="danger">URL already used</Text>
							) : slugStatus === "available" ? (
								<Text type="success">URL can be used</Text>
							) : (
								<span style={{ height: 27, display: "block" }}></span>
							)
						) : null}
					</Col>
					<Col
						span={24}
						style={{
							position: "relative",
						}}
					>
						<Space direction="vertical">
							<Typography.Text>
								Categories{" "}
								<span
									style={{
										color: "red",
									}}
								>
									*
								</span>
							</Typography.Text>
							<Form.Item
								name="categoryIds"
								// label="Categories"
								// rules={generateFormRules("Categories", ["required"])}
								style={{
									marginTop: 20,
								}}
							>
								<SelectCategories
									categories={categories}
									parentCategoryWatch={parentCategoryWatch}
									onSelectCategoryId={(catIds) => {
										form.setFieldValue("categoryIds", catIds);
										// resetErrorFormItem(form, "categoryIds", catIds);
									}}
									onSelectParentCategoryId={(parentIds) => {
										form.setFieldValue("parentCategoryIds", parentIds);
										// resetErrorFormItem(form, "parentCategoryIds", parentIds);
									}}
								/>
							</Form.Item>
						</Space>

						<Form.Item
							noStyle
							name="parentCategoryIds"
							// rules={generateFormRules("Categories", ["required"])}
						/>
						<Button
							style={{
								position: "absolute",
								top: 0,
								right: 12,
							}}
							icon={<PlusOutlined />}
							type="primary"
							onClick={() => setShowModalAddCategory(true)}
						>
							Add New Category
						</Button>
					</Col>
					<Col span={24}>
						<Form.Item
							name="images"
							label="Feature Image"
							rules={generateFormRules("Feature Image", ["required"])}
						>
							<UploadImageBlog
								titleBlog={titleWatch}
								onImage={(images) => {
									form.setFieldValue("images", images);
									resetErrorFormItem(form, "images", images);
								}}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="tagIds"
							label="Tags"
							rules={generateFormRules("Tags", ["required"])}
						>
							<Select
								mode="multiple"
								options={tags.map((tag) => ({
									label: tag.tagName,
									value: tag.tagId,
								}))}
								{...SELECT_SEARCH_PROPS}
								placeholder="Select tags..."
								notFoundContent={
									<Typography.Text
										style={{
											display: "flex",
											justifyContent: "center",
										}}
										type="secondary"
									>
										Tag is Empty.
									</Typography.Text>
								}
								dropdownRender={(menu) => (
									<>
										{menu}
										<Divider style={{ margin: "8px 0" }} />
										<Space style={{ padding: "0 8px 4px" }}>
											<Input
												placeholder="Enter a new tag name"
												ref={inputNewTagRef}
												value={newTag}
												onChange={onNameChange}
												onKeyDown={(e) => e.stopPropagation()}
												style={{
													width: 300,
												}}
											/>
											<Button
												type="text"
												icon={<PlusOutlined />}
												onClick={addNewTag}
											>
												Add New Tag
											</Button>
										</Space>
									</>
								)}
							/>
						</Form.Item>
					</Col>
					<Col
						sm={{ span: 12 }}
						md={{
							span: choosePublishWatch && choosePublishWatch === EPublishTypeArticle.SCHEDULED ? 6 : 8,
						}}
						span={24}
					>
						<Form.Item
							name="publishType"
							label="Choose Publish"
							rules={generateFormRules("Choose Publish", ["required"])}
						>
							<Radio.Group disabled={isEdit}>
								<Radio value={EPublishTypeArticle.PUBLISH_NOW}>Publish Now</Radio>
								<Radio value={EPublishTypeArticle.SCHEDULED}>Schedule Publish</Radio>
							</Radio.Group>
						</Form.Item>
					</Col>

					{choosePublishWatch && choosePublishWatch === EPublishTypeArticle.SCHEDULED && (
						<Col
							sm={{ span: 12 }}
							md={{
								span: 7,
							}}
							span={24}
						>
							<Form.Item
								name="publicationAt"
								label="Schedule Publish At"
								rules={generateFormRules("Schedule Publish At", ["required"])}
								initialValue={moment()}
							>
								<DatePicker
									format="YYYY-MM-DD HH:mm"
									disabledDate={disabledDate}
									showNow
									showTime
									disabled={isEdit}
								/>
							</Form.Item>
						</Col>
					)}

					<Col
						sm={{ span: 12 }}
						md={{
							span: choosePublishWatch && choosePublishWatch === EPublishTypeArticle.SCHEDULED ? 5 : 8,
						}}
						span={24}
					>
						<Form.Item
							name="author"
							label="Blog Admin"
						>
							<Typography.Text strong>
								{isEdit ? getFullName(post?.user) : user ? getFullName(user) : ""}
							</Typography.Text>
						</Form.Item>
					</Col>

					<Col
						sm={{ span: 12 }}
						md={{
							span: choosePublishWatch && choosePublishWatch === EPublishTypeArticle.SCHEDULED ? 6 : 8,
						}}
						span={24}
					>
						<Form.Item
							name="authorName"
							label="Author/Editor Name"
						>
							<Input placeholder="Enter custom author/editor name" />
						</Form.Item>
					</Col>

					<Col
						span={24}
						style={{ height: 20 }}
					></Col>

					<Col span={24}>
						<Row style={{ justifyContent: "space-between" }}>
							<Col></Col>
							<Col>
								<Space>
									<Button
										loading={isLoading}
										onClick={() => _handleSubmit("draft")}
										className="btn-secondary"
									>
										Save to Draft
									</Button>
									<Button
										type="primary"
										className="btn-fixed-lg"
										onClick={() => _handleSubmit("next")}
										loading={isLoading}
									>
										Next
									</Button>
								</Space>
							</Col>
						</Row>
					</Col>
				</Row>
			</Form>

			<CreateCategoryModal
				open={showModalAddCategory}
				onClose={() => setShowModalAddCategory(false)}
				onSuccess={() => {
					setIsFetch(true);
				}}
			/>
		</Spin>
	);
};

export default CreateDetailPost;
